import { isInvisiblySmallElement } from '@excalidraw/excalidraw';
import { participantsSelector } from '../../../store/slices/participantSlice';
import { store } from '../../../store';
import { sendWebsocketMessage } from '../../../helpers/websocket';
import { updateRequestedWhiteboardData } from '../../../store/slices/whiteboard';
import { PRECEDING_ELEMENT_KEY, } from './reconciliation';
import { DataMessage, DataMsgBodyType, DataMsgType, } from '../../../helpers/proto/plugnmeet_datamessage_pb';
const broadcastedElementVersions = new Map(), DELETED_ELEMENT_TIMEOUT = 3 * 60 * 60 * 1000; // 3 hours
let preScrollX = 0, preScrollY = 0;
export const sendRequestedForWhiteboardData = () => {
    const session = store.getState().session;
    const participants = participantsSelector
        .selectAll(store.getState())
        .filter((participant) => { var _a; return participant.sid !== ((_a = session.currentUser) === null || _a === void 0 ? void 0 : _a.sid); });
    if (!participants.length)
        return;
    participants.sort((a, b) => {
        return a.joinedAt - b.joinedAt;
    });
    let donors = participants;
    if (donors.length > 2) {
        // we'll request data from max 2 users.
        donors = participants.slice(0, 2);
    }
    donors.forEach((donor) => {
        var _a, _b, _c, _d;
        const dataMsg = new DataMessage({
            type: DataMsgType.SYSTEM,
            roomSid: session.currentRoom.sid,
            roomId: session.currentRoom.room_id,
            to: donor.sid,
            body: {
                type: DataMsgBodyType.INIT_WHITEBOARD,
                from: {
                    sid: (_b = (_a = session.currentUser) === null || _a === void 0 ? void 0 : _a.sid) !== null && _b !== void 0 ? _b : '',
                    userId: (_d = (_c = session.currentUser) === null || _c === void 0 ? void 0 : _c.userId) !== null && _d !== void 0 ? _d : '',
                },
                msg: '',
            },
        });
        sendWebsocketMessage(dataMsg.toBinary());
    });
};
export const sendWhiteboardDataAsDonor = (excalidrawAPI, sendTo) => {
    // broadcast page info first
    const whiteboard = store.getState().whiteboard;
    const currentFile = whiteboard.whiteboardUploadedOfficeFiles.filter((f) => f.fileId === whiteboard.currentWhiteboardOfficeFileId);
    if (!currentFile.length) {
        return;
    }
    const newFile = {
        fileId: currentFile[0].fileId,
        fileName: currentFile[0].fileName,
        filePath: currentFile[0].filePath,
        totalPages: currentFile[0].totalPages,
        currentPage: whiteboard.currentPage,
        pageFiles: whiteboard.whiteboardOfficeFilePagesAndOtherImages,
    };
    broadcastWhiteboardOfficeFile(newFile, sendTo);
    const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
    if (elements.length) {
        broadcastSceneOnChange(elements, true, sendTo);
    }
    // finally, change status of request
    store.dispatch(updateRequestedWhiteboardData({
        requested: false,
        sendTo: '',
    }));
};
export const isSyncableElement = (element) => {
    if (element.isDeleted) {
        return element.updated > Date.now() - DELETED_ELEMENT_TIMEOUT;
    }
    return !isInvisiblySmallElement(element);
};
export const broadcastSceneOnChange = (allElements, syncAll, sendTo) => {
    // sync out only the elements we think we need to save bandwidth.
    const syncableElements = allElements.reduce((acc, element, idx, elements) => {
        var _a;
        if ((syncAll ||
            !broadcastedElementVersions.has(element.id) ||
            //eslint-disable-next-line
            element.version > broadcastedElementVersions.get(element.id)) &&
            isSyncableElement(element)) {
            acc.push({
                ...element,
                // z-index info for the reconciler
                [PRECEDING_ELEMENT_KEY]: idx === 0 ? '^' : (_a = elements[idx - 1]) === null || _a === void 0 ? void 0 : _a.id,
            });
        }
        return acc;
    }, []);
    for (const syncableElement of syncableElements) {
        broadcastedElementVersions.set(syncableElement.id, syncableElement.version);
    }
    broadcastScreenDataBySocket(syncableElements, sendTo);
};
export const broadcastScreenDataBySocket = (elements, sendTo) => {
    var _a, _b, _c, _d;
    const session = store.getState().session;
    const dataMsg = new DataMessage({
        type: DataMsgType.WHITEBOARD,
        roomSid: session.currentRoom.sid,
        roomId: session.currentRoom.room_id,
        body: {
            type: DataMsgBodyType.SCENE_UPDATE,
            from: {
                sid: (_b = (_a = session.currentUser) === null || _a === void 0 ? void 0 : _a.sid) !== null && _b !== void 0 ? _b : '',
                userId: (_d = (_c = session.currentUser) === null || _c === void 0 ? void 0 : _c.userId) !== null && _d !== void 0 ? _d : '',
            },
            msg: JSON.stringify(elements),
        },
    });
    if (sendTo && sendTo !== '') {
        dataMsg.to = sendTo;
    }
    sendWebsocketMessage(dataMsg.toBinary());
};
export const broadcastCurrentPageNumber = (page, sendTo) => {
    var _a, _b, _c, _d;
    const session = store.getState().session;
    const dataMsg = new DataMessage({
        type: DataMsgType.WHITEBOARD,
        roomSid: session.currentRoom.sid,
        roomId: session.currentRoom.room_id,
        body: {
            type: DataMsgBodyType.PAGE_CHANGE,
            from: {
                sid: (_b = (_a = session.currentUser) === null || _a === void 0 ? void 0 : _a.sid) !== null && _b !== void 0 ? _b : '',
                userId: (_d = (_c = session.currentUser) === null || _c === void 0 ? void 0 : _c.userId) !== null && _d !== void 0 ? _d : '',
            },
            msg: `${page}`,
        },
    });
    if (sendTo !== '') {
        dataMsg.to = sendTo;
    }
    sendWebsocketMessage(dataMsg.toBinary());
};
export const broadcastWhiteboardOfficeFile = (newFile, sendTo) => {
    var _a, _b, _c, _d;
    const session = store.getState().session;
    const dataMsg = new DataMessage({
        type: DataMsgType.WHITEBOARD,
        roomSid: session.currentRoom.sid,
        roomId: session.currentRoom.room_id,
        body: {
            type: DataMsgBodyType.ADD_WHITEBOARD_OFFICE_FILE,
            from: {
                sid: (_b = (_a = session.currentUser) === null || _a === void 0 ? void 0 : _a.sid) !== null && _b !== void 0 ? _b : '',
                userId: (_d = (_c = session.currentUser) === null || _c === void 0 ? void 0 : _c.userId) !== null && _d !== void 0 ? _d : '',
            },
            msg: JSON.stringify(newFile),
        },
    });
    if (sendTo !== '') {
        dataMsg.to = sendTo;
    }
    sendWebsocketMessage(dataMsg.toBinary());
};
export const broadcastMousePointerUpdate = (msg) => {
    var _a, _b, _c, _d;
    const session = store.getState().session;
    const dataMsg = new DataMessage({
        type: DataMsgType.WHITEBOARD,
        roomSid: session.currentRoom.sid,
        roomId: session.currentRoom.room_id,
        body: {
            type: DataMsgBodyType.POINTER_UPDATE,
            from: {
                sid: (_b = (_a = session.currentUser) === null || _a === void 0 ? void 0 : _a.sid) !== null && _b !== void 0 ? _b : '',
                userId: (_d = (_c = session.currentUser) === null || _c === void 0 ? void 0 : _c.userId) !== null && _d !== void 0 ? _d : '',
            },
            msg: JSON.stringify(msg),
        },
    });
    sendWebsocketMessage(dataMsg.toBinary());
};
export const broadcastAppStateChanges = (height, width, scrollX, scrollY, zoomValue, theme, viewBackgroundColor, zenModeEnabled, gridSize) => {
    var _a, _b, _c, _d;
    if (preScrollX === scrollX && preScrollY === scrollY) {
        // if both same then we don't need to update
        return;
    }
    else {
        preScrollX = scrollX;
        preScrollY = scrollY;
    }
    const session = store.getState().session;
    const dataMsg = new DataMessage({
        type: DataMsgType.WHITEBOARD,
        roomSid: session.currentRoom.sid,
        roomId: session.currentRoom.room_id,
        body: {
            type: DataMsgBodyType.WHITEBOARD_APP_STATE_CHANGE,
            from: {
                sid: (_b = (_a = session.currentUser) === null || _a === void 0 ? void 0 : _a.sid) !== null && _b !== void 0 ? _b : '',
                userId: (_d = (_c = session.currentUser) === null || _c === void 0 ? void 0 : _c.userId) !== null && _d !== void 0 ? _d : '',
            },
            msg: JSON.stringify({
                height,
                width,
                scrollX,
                scrollY,
                zoomValue,
                theme,
                viewBackgroundColor,
                zenModeEnabled,
                gridSize,
            }),
        },
    });
    sendWebsocketMessage(dataMsg.toBinary());
};
